import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Trophy, Stethoscope, CheckCircle, AlertTriangle, Palmtree, Pencil, FileSpreadsheet, FileText, CalendarRange, Calendar, CalendarDays } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const REFRESH_INTERVAL_MS = 600000;
const PDF_TITLE_SIZE = 18;
const PDF_SUBTITLE_SIZE = 10;
const PDF_TITLE_X = 14;
const PDF_TITLE_Y = 22;
const PDF_DATE_Y = 30;
const PDF_TABLE_START_Y = 35;
const PDF_TABLE_FONT_SIZE = 7;
const PDF_HEADER_COLOR = [0, 32, 91];
const COL_MIN_WIDTH = 10;

const PRODUCTS = [
  "Fonasa", "Zona", "1727", "Previsional", "Seg. Viaje", "Medis Group", "RXD + 18"
];

const SHORT_NAMES = {
  "Fonasa": "Fonasa", "Zona": "Zona", "1727": "1727",
  "Previsional": "Previsional", "Seg. Viaje": "Seg. Viaje", "Medis Group": "Medis", "RXD + 18": "RXD+18"
};

const PERIODOS = [
  { key: 'semanal', label: 'Semanal', icon: CalendarDays },
  { key: 'mensual', label: 'Mensual', icon: Calendar },
  { key: 'anual', label: 'Anual', icon: CalendarRange },
];

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

export default function Ranking() {
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mensual');
  const [mes, setMes] = useState(new Date().getMonth() + 1);
  const [año, setAño] = useState(new Date().getFullYear());
  const [editingVendor, setEditingVendor] = useState(null);
  
  const [certificadoEnfermedad, setCertificadoEnfermedad] = useState(false);
  const [asistenciaCompleta, setAsistenciaCompleta] = useState(true);
  const [tieneSanciones, setTieneSanciones] = useState(false);
  const [estaVacaciones, setEstaVacaciones] = useState(false);
  const [diasFaltaMes, setDiasFaltaMes] = useState(0);
  const [notasEstado, setNotasEstado] = useState('');

  const fetchRanking = useCallback(async () => {
    try {
      setLoading(true);
      const qMes = periodo === 'mensual' ? mes : null;
      const qAño = periodo !== 'semanal' ? año : null;
      const data = await api.getVendorsRanking(qMes, qAño, periodo);
      setRanking(data);
    } catch (error) {
      /* handled */
    } finally {
      setLoading(false);
    }
  }, [periodo, mes, año]);

  useEffect(() => {
    fetchRanking();
    const interval = setInterval(fetchRanking, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchRanking]);

  const openEditDialog = (vendor) => {
    setEditingVendor(vendor);
    setCertificadoEnfermedad(vendor.certificado_enfermedad || false);
    setAsistenciaCompleta(vendor.asistencia_completa !== false);
    setTieneSanciones(vendor.tiene_sanciones || false);
    setEstaVacaciones(vendor.esta_vacaciones || false);
    setDiasFaltaMes(vendor.dias_falta_mes || 0);
    setNotasEstado(vendor.notas_estado || '');
  };

  const handleUpdateStatus = async () => {
    try {
      await api.updateVendorStatus(editingVendor.id, {
        certificado_enfermedad: certificadoEnfermedad,
        asistencia_completa: asistenciaCompleta,
        tiene_sanciones: tieneSanciones,
        esta_vacaciones: estaVacaciones,
        dias_falta_mes: parseInt(diasFaltaMes) || 0,
        notas_estado: notasEstado
      });
      toast.success('Estado actualizado');
      setEditingVendor(null);
      fetchRanking();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const getMedalColor = (index) => {
    if (index === 0) return 'text-yellow-500';
    if (index === 1) return 'text-gray-400';
    if (index === 2) return 'text-amber-600';
    return 'text-gray-300';
  };

  const getStatusText = (vendor) => {
    const statuses = [];
    if (vendor.certificado_enfermedad) statuses.push('Certificado');
    if (!vendor.asistencia_completa) statuses.push('Faltas');
    if (vendor.tiene_sanciones) statuses.push('Sancion');
    if (vendor.esta_vacaciones) statuses.push('Vacaciones');
    if (vendor.dias_falta_mes > 0) statuses.push(`${vendor.dias_falta_mes} dias falta`);
    return statuses.length > 0 ? statuses.join(', ') : 'OK';
  };

  const exportToExcel = () => {
    const data = ranking.map((v, i) => {
      const row = { '#': i + 1, 'Vendedor': v.nombre, 'Ficha': v.ficha_casmu };
      PRODUCTS.forEach(p => { row[p] = v.por_producto?.[p] || 0; });
      row['Estado'] = getStatusText(v);
      return row;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ranking');
    const colWidths = Object.keys(data[0] || {}).map(key => ({ wch: Math.max(key.length, COL_MIN_WIDTH) }));
    ws['!cols'] = colWidths;
    XLSX.writeFile(wb, `ranking_${periodo}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado');
  };

  const exportToPDF = () => {
    const doc = new jsPDF('landscape');
    doc.setFontSize(PDF_TITLE_SIZE);
    const periodoLabel = periodo === 'semanal' ? 'Semanal' : periodo === 'anual' ? 'Anual' : 'Mensual';
    doc.text(`Ranking de Vendedores - ${periodoLabel}`, PDF_TITLE_X, PDF_TITLE_Y);
    doc.setFontSize(PDF_SUBTITLE_SIZE);
    doc.text(`Generado: ${new Date().toLocaleDateString('es-UY')}`, PDF_TITLE_X, PDF_DATE_Y);
    const headers = ['#', 'Vendedor', 'Ficha', 'Total', ...PRODUCTS, 'Estado'];
    const tableData = ranking.map((v, i) => [
      i + 1, v.nombre, v.ficha_casmu, v.total_servicios,
      ...PRODUCTS.map(p => v.por_producto?.[p] || 0),
      getStatusText(v)
    ]);
    autoTable(doc, {
      head: [headers], body: tableData, startY: PDF_TABLE_START_Y,
      styles: { fontSize: PDF_TABLE_FONT_SIZE, cellPadding: 2 },
      headStyles: { fillColor: PDF_HEADER_COLOR, fontSize: 6 },
      columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 30 }, 2: { cellWidth: 18 } }
    });
    doc.save(`ranking_${periodo}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success('PDF exportado');
  };

  return (
    <div className="space-y-6" data-testid="ranking">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0A0A0A]">Ranking de Vendedores</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Rendimiento por periodo de cada vendedor</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportToExcel} className="btn-secondary flex items-center gap-2" data-testid="export-excel">
            <FileSpreadsheet size={16} /> Excel
          </button>
          <button onClick={exportToPDF} className="btn-secondary flex items-center gap-2" data-testid="export-pdf">
            <FileText size={16} /> PDF
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap" data-testid="ranking-filters">
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg" data-testid="ranking-periodo-tabs">
          {PERIODOS.map(p => {
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                onClick={() => setPeriodo(p.key)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-[13px] font-medium transition-all ${
                  periodo === p.key
                    ? 'bg-white text-[#00205B] shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`ranking-tab-${p.key}`}
              >
                <Icon size={14} />
                {p.label}
              </button>
            );
          })}
        </div>

        {periodo === 'mensual' && (
          <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '140px'}} data-testid="ranking-mes-select">
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        )}

        {periodo !== 'semanal' && (
          <select value={año} onChange={(e) => setAño(parseInt(e.target.value))} className="form-input text-[13px] py-2" style={{width: '90px'}} data-testid="ranking-año-select">
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        )}
      </div>

      <div className="mac-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-header w-12">#</th>
                <th className="table-header">Vendedor</th>
                {PRODUCTS.map(p => (
                  <th key={p} className="table-header text-center text-[10px] px-1.5" title={p}>{SHORT_NAMES[p]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={3 + PRODUCTS.length} className="table-cell text-center text-gray-500">Cargando...</td></tr>
              ) : ranking.length === 0 ? (
                <tr><td colSpan={3 + PRODUCTS.length} className="table-cell text-center text-gray-500">No hay vendedores</td></tr>
              ) : (
                ranking.map((vendor, index) => (
                  <tr key={vendor.id} className="table-row">
                    <td className="table-cell">
                      <div className="flex items-center justify-center">
                        {index < 3 ? (
                          <Trophy size={18} className={getMedalColor(index)} fill="currentColor" />
                        ) : (
                          <span className="text-gray-400 font-medium">{index + 1}</span>
                        )}
                      </div>
                    </td>
                    <td className="table-cell">
                      <div className="font-medium text-[#0A0A0A] text-[13px]">{vendor.nombre}</div>
                      <div className="text-[10px] text-gray-500">{vendor.ficha_casmu}</div>
                    </td>
                    {PRODUCTS.map(p => {
                      const count = vendor.por_producto?.[p] || 0;
                      return (
                        <td key={p} className="table-cell text-center px-1.5">
                          {count > 0 ? (
                            <span className="inline-flex items-center justify-center min-w-[24px] px-1 py-0.5 rounded bg-[#0082D2]/10 text-[#0082D2] text-[11px] font-bold">{count}</span>
                          ) : (
                            <span className="text-gray-300 text-[11px]">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editingVendor} onOpenChange={(open) => !open && setEditingVendor(null)}>
        <DialogContent className="mac-popover max-w-md p-0">
          <DialogHeader className="px-5 py-4 border-b border-gray-200">
            <DialogTitle className="text-[15px] font-semibold text-[#0A0A0A]">
              Estado de {editingVendor?.nombre}
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            <div className="bg-orange-50 rounded-md p-4">
              <label className="form-label text-orange-800">Dias de Falta en el Mes</label>
              <input type="number" min="0" max="31" value={diasFaltaMes} onChange={(e) => setDiasFaltaMes(e.target.value)} className="form-input mt-1" placeholder="0" data-testid="dias-falta-input" />
            </div>

            <div className="space-y-3">
              <label className="flex items-center gap-3 p-3 bg-red-50 rounded-md cursor-pointer">
                <Checkbox checked={certificadoEnfermedad} onCheckedChange={setCertificadoEnfermedad} className="rounded" />
                <div className="flex items-center gap-2">
                  <Stethoscope size={16} className="text-red-600" />
                  <span className="text-[13px] font-medium text-gray-700">Certificado por enfermedad</span>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-blue-50 rounded-md cursor-pointer">
                <Checkbox checked={asistenciaCompleta} onCheckedChange={setAsistenciaCompleta} className="rounded" />
                <div className="flex items-center gap-2">
                  <CheckCircle size={16} className="text-blue-600" />
                  <span className="text-[13px] font-medium text-gray-700">Asistencia completa (fue todos los dias)</span>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-yellow-50 rounded-md cursor-pointer">
                <Checkbox checked={tieneSanciones} onCheckedChange={setTieneSanciones} className="rounded" />
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-yellow-600" />
                  <span className="text-[13px] font-medium text-gray-700">Tiene sanciones</span>
                </div>
              </label>
              
              <label className="flex items-center gap-3 p-3 bg-green-50 rounded-md cursor-pointer">
                <Checkbox checked={estaVacaciones} onCheckedChange={setEstaVacaciones} className="rounded" />
                <div className="flex items-center gap-2">
                  <Palmtree size={16} className="text-green-600" />
                  <span className="text-[13px] font-medium text-gray-700">Esta de vacaciones</span>
                </div>
              </label>
            </div>
            
            <div>
              <label className="form-label">Notas</label>
              <textarea value={notasEstado} onChange={(e) => setNotasEstado(e.target.value)} className="form-input min-h-[80px] resize-none" placeholder="Notas adicionales..." />
            </div>
            
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setEditingVendor(null)} className="btn-secondary">Cancelar</button>
              <button onClick={handleUpdateStatus} className="btn-primary" data-testid="save-status-btn">Guardar</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
